/**
 * Shared visual primitives for the NFL sabotage store (SAB-540 C3/C3b).
 * Used by NFLSabotagesPage (store + arsenal), SabotageDeployDialog, and the
 * matchup-view Drop CTA so they stay visually identical.
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/* Overlays portal to document.body so their `fixed` positioning resolves against
 * the viewport, not a transformed/overflow-hidden ancestor (e.g. a matchup card).
 * Without this, an overlay mounted inside such a card renders inline + clipped. */
const portal = (node: React.ReactNode) =>
  (typeof document !== 'undefined' ? createPortal(node, document.body) : node);

/* Rarity styling: design tokens (common gray, rare blue, legendary purple). */
export const RARITY = {
  common: {
    label: 'Common',
    text: 'text-[hsl(215,20%,65%)]',
    swatch: 'bg-[hsl(215,20%,55%)]',
    border: 'border-[hsl(215,20%,55%/0.3)]',
    tile: 'bg-card',
    chip: 'bg-[hsl(215,20%,55%/0.12)] border-[hsl(215,20%,55%/0.4)]',
    badge: 'bg-[hsl(215,20%,55%/0.18)] text-[hsl(215,20%,70%)] border-[hsl(215,20%,55%/0.4)]',
    glow: '',
  },
  rare: {
    label: 'Rare',
    text: 'text-[hsl(220,90%,66%)]',
    swatch: 'bg-[hsl(220,90%,56%)]',
    border: 'border-[hsl(220,90%,56%/0.4)]',
    tile: 'bg-[hsl(220,90%,56%/0.06)]',
    chip: 'bg-[hsl(220,90%,56%/0.14)] border-[hsl(220,90%,56%/0.45)]',
    badge: 'bg-[hsl(220,90%,56%/0.18)] text-[hsl(220,90%,70%)] border-[hsl(220,90%,56%/0.45)]',
    glow: 'shadow-[0_0_24px_hsl(220,90%,56%/0.12)]',
  },
  legendary: {
    label: 'Legendary',
    text: 'text-[hsl(280,85%,70%)]',
    swatch: 'bg-[hsl(280,85%,60%)]',
    border: 'border-[hsl(280,85%,60%/0.45)]',
    tile: 'bg-[hsl(280,85%,60%/0.07)]',
    chip: 'bg-[hsl(280,85%,60%/0.16)] border-[hsl(280,85%,60%/0.5)]',
    badge: 'bg-[hsl(280,85%,60%/0.2)] text-[hsl(280,85%,72%)] border-[hsl(280,85%,60%/0.5)]',
    glow: 'shadow-[0_0_30px_hsl(280,85%,60%/0.16)]',
  },
} as const;

export type RarityKey = keyof typeof RARITY;
export const rarityOf = (r: string): RarityKey => (r in RARITY ? (r as RarityKey) : 'common');

export const SABOTAGE_GRADIENT = 'var(--gradient-sabotage, linear-gradient(135deg,hsl(25 95% 53%),hsl(35 100% 50%)))';
export const DAMAGE_GRADIENT = 'var(--gradient-damage, linear-gradient(135deg,hsl(0 84% 60%),hsl(15 90% 55%)))';

export const usd = (cents: number | null | undefined) =>
  cents == null ? '' : `$${(cents / 100).toFixed(2)}`;

export const shortExpiry = (target: string | null): string => {
  if (!target) return '';
  const ms = new Date(target).getTime() - Date.now();
  if (ms <= 0) return 'locked';
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
};

/* Live countdown to a lock time (matchup lock / store close). */
export function useCountdown(target: string | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return 'TBD';
  let ms = new Date(target).getTime() - now;
  if (ms <= 0) return 'Locked';
  const d = Math.floor(ms / 86400000); ms -= d * 86400000;
  const h = Math.floor(ms / 3600000); ms -= h * 3600000;
  const m = Math.floor(ms / 60000); ms -= m * 60000;
  const s = Math.floor(ms / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return d > 0 ? `${d}d ${pad(h)}h ${pad(m)}m ${pad(s)}s` : `${pad(h)}h ${pad(m)}m ${pad(s)}s`;
}

export function ArtChip({ emoji, r, size = 44 }: { emoji: string | null; r: RarityKey; size?: number }) {
  return (
    <span
      className={cn('shrink-0 rounded-xl flex items-center justify-center border', RARITY[r].chip, RARITY[r].glow)}
      style={{ width: size, height: size, fontSize: size * 0.5 }}
    >
      {emoji || '💣'}
    </span>
  );
}

export function Scrim({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return portal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[rgba(3,5,10,0.74)] backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        className="w-[min(94vw,440px)] rounded-[22px] bg-[hsl(222,47%,10%)] border border-border p-5 shadow-[0_30px_70px_rgba(0,0,0,0.6)] animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

export function Takeover({ tint, children }: { tint: 'orange' | 'red'; children: React.ReactNode }) {
  const glow = tint === 'orange' ? 'hsl(25,95%,53%/0.18)' : 'hsl(0,84%,60%/0.18)';
  return portal(
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center text-center px-6 animate-in fade-in duration-200"
      style={{ background: `radial-gradient(circle at 50% 40%, ${glow}, hsl(222,47%,6%) 60%)` }}
    >
      {children}
    </div>
  );
}
